import { ReactElement, useEffect } from 'react';

import { AddressComponentSwitcher } from '@zooplus/customer-data-frontend';

import CheckOrderAndDeliveryOption from '../check-order-and-delivery-options';
import CustomerInformation from '../customer-information';
import PaymentComponent from '../payment-component/payment-component';

import { CUSTOMER_TYPE } from '@utilities/constants';
import { isObjectNotNullOrEmpty } from '@utilities/utils';

import { ContainerType } from './preview-containers-section';

const emailItem: ContainerType = {
  name: 'checkout.preview.customer.email.address',
  Content: ({ isContainerActive, setComplete, checkoutContext }): ReactElement => {
    const { checkoutState } = checkoutContext;
    const customerId = checkoutState?.customer?.customerId;

    useEffect(() => {
      const shouldSetComplete = !!customerId;
      setComplete?.(shouldSetComplete, shouldSetComplete);
    }, [customerId]);

    return <CustomerInformation setComplete={setComplete} isContainerActive={isContainerActive} />;
  },
  dataTestId: 'email-address-container',
  edit: (checkoutContext) => {
    const { checkoutState } = checkoutContext;
    const customerType = checkoutState?.customer?.customerType;
    const isExistingCustomer = customerType === CUSTOMER_TYPE.EXISTING_CUSTOMER;
    const isNewCustomer = customerType === CUSTOMER_TYPE.NEW_CUSTOMER;

    return !(isExistingCustomer || isNewCustomer);
  },
  error: ({ checkoutContext }) => {
    const { checkoutState } = checkoutContext;
    const error = !!(
      isObjectNotNullOrEmpty(checkoutState?.stateMessages) &&
      checkoutState?.stateMessages?.customerMessages?.length
    );
    return error;
  },
};

const deliveryAndInvoiceItem: ContainerType = {
  name: 'checkout.preview.deliveryAndInvoice.addresses',
  Content: ({
    isContainerActive,
    setComplete,
    isContainerComplete,
    setCurrentStepActive,
    checkoutContext,
  }): ReactElement => {
    const { checkoutState, isDeliveryAndInvoiceCollapsed, setIsDeliveryAndInvoiceCollapsed } =
      checkoutContext;

    const isCustomerAddressPresent = !!(
      isObjectNotNullOrEmpty(checkoutState?.customer?.deliveryAddress) &&
      isObjectNotNullOrEmpty(checkoutState?.customer?.invoiceAddress)
    );

    useEffect(() => {
      setComplete?.(isCustomerAddressPresent);
      setIsDeliveryAndInvoiceCollapsed?.(isCustomerAddressPresent ?? true);
    }, []);

    useEffect(() => {
      if (isCustomerAddressPresent && isDeliveryAndInvoiceCollapsed && isContainerActive) {
        setComplete?.(true);
      }
      !isDeliveryAndInvoiceCollapsed && setCurrentStepActive?.();
    }, [isDeliveryAndInvoiceCollapsed, isCustomerAddressPresent]);

    useEffect(() => {
      setIsDeliveryAndInvoiceCollapsed?.(!isContainerActive);
    }, [isContainerActive]);

    return isContainerActive || isContainerComplete ? <AddressComponentSwitcher /> : <></>;
  },
  dataTestId: 'address-container',
  edit: false,
  error: ({ checkoutContext }) => {
    const { checkoutState } = checkoutContext;
    const error = !!(
      isObjectNotNullOrEmpty(checkoutState?.stateMessages) &&
      isObjectNotNullOrEmpty(checkoutState?.stateMessages?.addressMessages)
    );
    return error;
  },
};

const paymentItem: ContainerType = {
  name: 'checkout.preview.payment.method',
  Content: ({
    isContainerActive,
    setComplete,
    isContainerComplete,
    checkoutContext,
  }): ReactElement => {
    const { checkoutState, isPaymentCollapsed, setIsPaymentCollapsed } = checkoutContext;

    const isPaymentPresent = !!(
      isObjectNotNullOrEmpty(checkoutState?.payment) &&
      isObjectNotNullOrEmpty(checkoutState?.payment?.preferredPayment)
    );

    useEffect(() => {
      setComplete?.(isPaymentPresent);
      setIsPaymentCollapsed?.(isPaymentPresent ?? true);
    }, []);

    useEffect(() => {
      if (isPaymentPresent && isPaymentCollapsed && isContainerActive) {
        setComplete?.(true);
      }
    }, [isPaymentCollapsed, isPaymentPresent]);

    useEffect(() => {
      setIsPaymentCollapsed?.(!isContainerActive);
    }, [isContainerActive]);

    return isContainerActive || isContainerComplete ? <PaymentComponent /> : <></>;
  },
  dataTestId: 'payment-container',
  edit: true,
  error: ({ checkoutContext }) => {
    const { checkoutState } = checkoutContext;
    const error = !!(
      isObjectNotNullOrEmpty(checkoutState?.stateMessages) &&
      isObjectNotNullOrEmpty(checkoutState?.stateMessages?.paymentMessages)
    );
    return error;
  },
};

const checkOrderAndDeliveryOptionItem: ContainerType = {
  name: 'checkout.preview.checkOrder.deliveryOption',
  Content: ({
    isContainerActive,
    isContainerComplete,
    setComplete,
    arePrevContainersCompleted,
  }): ReactElement => {
    useEffect(() => {
      setComplete?.(arePrevContainersCompleted, false);
    }, [arePrevContainersCompleted]);

    return isContainerActive || isContainerComplete ? (
      <CheckOrderAndDeliveryOption isContainerActive={isContainerActive} />
    ) : (
      <></>
    );
  },
  dataTestId: 'check-order-and-delivery-option-container',
  edit: {
    name: 'checkout.preview.edit.basket',
    shouldDisplayAlways: true,
  },
  error: () => false,
};

export const containers: ContainerType[] = [
  emailItem,
  deliveryAndInvoiceItem,
  paymentItem,
  checkOrderAndDeliveryOptionItem,
];
